import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import UnitForm from "./UnitForm";
import UnitList from "./UnitList";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import SystemValuePicker from "../../../../components/SystemValue/SystemValuePicker";
import {
  UNIT_CATALOG,
  buildUnitBody,
} from "../../../../utils/constants/systemValueCatalog";
import { bulkCreateSystemValues } from "../../../../utils/bulkCreateSystemValues";
import { toast } from "../../../../components/Toast/toast";
import { btn } from "../../../../components/Button/buttonStyles";

const UnitPage = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [adding, setAdding] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const res = await api.get("/admin-units");
      setUnits(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch units");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      const res = await api.put(`/admin-units/${editId}`, formData);
      setUnits(
        units.map((u) => (String(u.id) === String(editId) ? res.data.data : u))
      );
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save unit");
    }
  };

  const handleAdd = async (names) => {
    setAdding(true);
    const { created, failed } = await bulkCreateSystemValues(
      "/admin-units",
      names,
      buildUnitBody
    );
    setAdding(false);

    if (created.length) setUnits((prev) => [...prev, ...created]);
    if (failed.length) toast.error(`Could not add: ${failed.join(", ")}`);
    if (created.length) setAction(null);
  };

  const handleDelete = async () => {
    if (!unitToDelete) return;
    try {
      await api.delete(`/admin-units/${unitToDelete}`);
      setUnits(units.filter((u) => u.id !== unitToDelete));
      setUnitToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete unit");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingUnit =
    action === "edit" && editId
      ? units.find((u) => String(u.id) === String(editId))
      : null;

  return (
    <>
      {/* The picker lays its catalog out in two columns, so it needs the room. */}
      <div className={action === "add" ? "md:w-2/3" : "md:w-1/2"}>
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Units</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm")}
            >
              Add Unit
            </button>
          )}
        </div>

        {error && <p className="text-danger-600 text-sm mb-3">{error}</p>}

        {action === "add" ? (
          <SystemValuePicker
            catalog={UNIT_CATALOG}
            existingNames={units.map((u) => u.name)}
            onAdd={handleAdd}
            onCancel={() => setAction(null)}
            label="unit"
            labelPlural="units"
            addOtherLabel="Add Other Unit"
            submitting={adding}
          />
        ) : inForm ? (
          <UnitForm
            unitToEdit={editingUnit}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <UnitList
            units={units}
            onEdit={(unit) => setAction("edit", unit.id)}
            onDelete={(id) => setUnitToDelete(id)}
          />
        )}
      </div>

      {unitToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this unit? This action cannot be undone."
          onCancel={() => setUnitToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
        />
      )}
    </>
  );
};

export default UnitPage;
